import { useNavigate, useParams, Link } from "react-router-dom";
import { useMe } from "../api/hooks/useMe";
import { useInvitePreview } from "../api/hooks/useInvitePreview";
import { useAcceptInvite } from "./useAcceptInvite";
import { AuthShell } from "../auth/AuthShell";
import { Button } from "../ui/Button";
import { Banner } from "../ui/Banner";
import { Spinner } from "../ui/Spinner";
import { useTranslation, apiErrorMessage } from "../i18n";
import { wsHome } from "../lib/paths";

/** Translation function, scoped to a namespace (from `useTranslation`). */
type TFn = (key: string, opts?: Record<string, unknown>) => string;

// Role label helper — `t` scoped to the "workspace" namespace.
function roleLabel(role: string, t: TFn): string {
  switch (role) {
    case "owner":  return t("accept.roleOwner");
    case "member": return t("accept.roleMember");
    case "viewer": return t("accept.roleViewer");
    default:       return role;
  }
}

// ── Preview card shared between all states ────────────────────────────────────

interface PreviewCardProps {
  readonly workspaceName: string;
  readonly role: string;
  readonly invitedBy?: string;
}

function PreviewCard({ workspaceName, role, invitedBy }: PreviewCardProps) {
  const { t } = useTranslation("workspace");
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/50 px-5 py-4">
      <p className="text-sm text-slate-400">
        {invitedBy ? t("accept.invitedByYou", { invitedBy }) : t("accept.invitedGeneric")}
      </p>
      <p className="mt-1 text-lg font-semibold text-slate-100">{workspaceName}</p>
      <p className="mt-0.5 text-sm text-slate-400">
        {t("accept.asRolePrefix")} <span className="text-slate-200">{roleLabel(role, t)}</span>
      </p>
    </div>
  );
}

// ── Unauthenticated state ─────────────────────────────────────────────────────

interface UnauthenticatedViewProps {
  readonly token: string;
  readonly workspaceName: string;
  readonly role: string;
  readonly invitedBy?: string;
}

function UnauthenticatedView({
  token,
  workspaceName,
  role,
  invitedBy,
}: UnauthenticatedViewProps) {
  const { t } = useTranslation("workspace");
  const returnTo = `/invite/${token}`;

  return (
    <AuthShell>
      <PreviewCard
        workspaceName={workspaceName}
        role={role}
        invitedBy={invitedBy}
      />

      <div className="mt-6 space-y-3">
        <Button
          variant="primary"
          className="w-full"
          onClick={() => {
            window.location.href = `/sign-in?next=${encodeURIComponent(returnTo)}`;
          }}
        >
          {t("accept.signInToAccept")}
        </Button>
        <Button
          variant="secondary"
          className="w-full"
          onClick={() => {
            window.location.href = `/sign-up?next=${encodeURIComponent(returnTo)}`;
          }}
        >
          {t("accept.signUpToAccept")}
        </Button>
      </div>
    </AuthShell>
  );
}

// ── Already-member state ──────────────────────────────────────────────────────

interface AlreadyMemberViewProps {
  readonly workspaceName: string;
  readonly workspaceSlug: string;
}

function AlreadyMemberView({ workspaceName, workspaceSlug }: AlreadyMemberViewProps) {
  const { t } = useTranslation("workspace");
  return (
    <AuthShell>
      <div className="text-center">
        <p className="text-base font-semibold text-slate-100">
          {t("accept.alreadyInPrefix")} <span className="text-arch-400">{workspaceName}</span>
        </p>
        <p className="mt-1 text-sm text-slate-400">
          {t("accept.alreadyInBody")}
        </p>
      </div>
      <div className="mt-6">
        <Link
          to={wsHome(workspaceSlug)}
          className="flex w-full items-center justify-center rounded-md bg-arch-600 px-4 py-2 text-sm font-medium text-white hover:bg-arch-500 focus:outline-none focus:ring-2 focus:ring-arch-400"
        >
          {t("accept.goToWorkspace")}
        </Link>
      </div>
    </AuthShell>
  );
}

// ── Accept invite state (signed in, not yet a member) ────────────────────────

interface AcceptViewProps {
  readonly token: string;
  readonly workspaceName: string;
  readonly workspaceSlug: string;
  readonly role: string;
  readonly invitedBy?: string;
  readonly expired: boolean;
}

function AcceptView({
  token,
  workspaceName,
  workspaceSlug,
  role,
  invitedBy,
  expired,
}: AcceptViewProps) {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const navigate = useNavigate();
  const accept = useAcceptInvite(token);

  function handleAccept() {
    accept.mutate(undefined, {
      onSuccess: (result) => {
        // Navigate to the workspace regardless of alreadyMember
        navigate(wsHome(workspaceSlug), { replace: true });
        // Suppress unused warning — result used via navigate above
        void result;
      },
    });
  }

  return (
    <AuthShell>
      <PreviewCard
        workspaceName={workspaceName}
        role={role}
        invitedBy={invitedBy}
      />

      {expired && (
        <Banner kind="error" className="mt-4">
          {t("accept.expired")}
        </Banner>
      )}

      {accept.error && !expired && (
        <Banner kind="error" className="mt-4">
          {accept.error.code === "conflict"
            ? t("accept.alreadyAccepted")
            : apiErrorMessage(accept.error, tErr)}
        </Banner>
      )}

      <div className="mt-6">
        <Button
          variant="primary"
          className="w-full"
          disabled={expired || accept.isPending}
          onClick={handleAccept}
        >
          {accept.isPending ? t("accept.accepting") : t("accept.accept")}
        </Button>
      </div>
    </AuthShell>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function AcceptInvitePage() {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const { token } = useParams<{ token: string }>();
  const me = useMe();
  const preview = useInvitePreview(token ?? "");

  // Still loading auth state or preview
  if (me.isLoading || preview.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <Spinner />
      </div>
    );
  }

  // Preview fetch failed — show error
  if (preview.isError) {
    const code = preview.error.code;
    return (
      <AuthShell>
        <div className="text-center">
          {code === "not_found" ? (
            <>
              <p className="text-base font-semibold text-slate-100">{t("accept.notFoundTitle")}</p>
              <p className="mt-1 text-sm text-slate-400">
                {t("accept.notFoundBody")}
              </p>
            </>
          ) : code === "conflict" ? (
            <>
              <p className="text-base font-semibold text-slate-100">{t("accept.expiredTitle")}</p>
              <p className="mt-1 text-sm text-slate-400">
                {t("accept.expiredBody")}
              </p>
            </>
          ) : (
            <>
              <p className="text-base font-semibold text-slate-100">{t("accept.errorTitle")}</p>
              <p className="mt-1 text-sm text-slate-400">{apiErrorMessage(preview.error, tErr)}</p>
            </>
          )}
        </div>
      </AuthShell>
    );
  }

  if (!preview.data || !token) {
    return null;
  }

  const {
    workspaceName,
    workspaceSlug,
    role,
    invitedBy,
    expired = false,
    alreadyMember = false,
  } = preview.data;

  // Unauthenticated: show preview + sign-in / sign-up CTAs
  if (!me.data) {
    return (
      <UnauthenticatedView
        token={token}
        workspaceName={workspaceName}
        role={role}
        invitedBy={invitedBy}
      />
    );
  }

  // Already a member (preview indicates this)
  if (alreadyMember) {
    return (
      <AlreadyMemberView
        workspaceName={workspaceName}
        workspaceSlug={workspaceSlug}
      />
    );
  }

  // Signed in and not yet a member — show accept button
  return (
    <AcceptView
      token={token}
      workspaceName={workspaceName}
      workspaceSlug={workspaceSlug}
      role={role}
      invitedBy={invitedBy}
      expired={expired}
    />
  );
}
